'use client';
import React, { useEffect, useState } from 'react';
import DashboardSidebar from './DashboardSidebar';
import DashboardCard from './DashboardCard';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  FavouriteIcon,
  GiftIcon,
  Logout01Icon,
  ProductLoadingIcon,
  UserAccountIcon,
  Shield01Icon,
  Coupon02Icon,
} from '@hugeicons/core-free-icons';
import DashboardContentRenderer, {
  DashboardCardName,
} from './DashboardContentRenderer';
import { useAuth } from '../authentication/hooks/auth';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
// 👇 Sidebar items with slug and name

const dashboardSidebarItem: { name: DashboardCardName; slug: string }[] = [
  { name: 'My Orders', slug: 'my-orders' },
  { name: 'Account Details', slug: 'account-details' },
  { name: 'Verify Product', slug: 'verify-product' },
  { name: 'My Coupons', slug: 'my-coupons' },
  { name: 'My WishList', slug: 'my-wishlist' },
  { name: 'My Rewards', slug: 'my-rewards' },
];

// 👇 Slug-to-name mapping
const slugToNameMap: Record<string, DashboardCardName> = {
  'my-orders': 'My Orders',
  'account-details': 'Account Details',
  'verify-product': 'Verify Product',
  'my-coupons': 'My Coupons',
  'my-wishlist': 'My WishList',
  'my-rewards': 'My Rewards',
};

const UserDashboard = () => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const viewParam = searchParams.get('view');
  const [activeCard, setActiveCard] = useState<DashboardCardName | null>(null);

  useEffect(() => {
    if (viewParam && slugToNameMap[viewParam]) {
      setActiveCard(slugToNameMap[viewParam]);
    } else {
      setActiveCard(null);
    }
  }, [viewParam]);

  const handleNavigation = (slug: string | null) => {
    if (slug === 'my-wishlist') return router.push(`/wishlist`);
    const url = slug ? `/account?view=${slug}` : `/account`;
    router.push(url);
  };

  const { user, logout } = useAuth();

  return (
    <>
      <section className="page-header">
        <div className="container">
          <div className="page-header__content">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb justify-content-start mb-0">
                <li className="breadcrumb-item ">
                  <Link href="/">Home</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Dashboard
                </li>
              </ol>
            </nav>
          </div>
        </div>
      </section>

      <section className="dashboard padding-bottom">
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-3">
              <DashboardSidebar
                activeCard={activeCard}
                onNavigate={handleNavigation}
              />
            </div>

            <div className="col-lg-9">
              <DashboardContentRenderer activeCard={activeCard} />
              {!activeCard && (
                <div className="dashboard__content">
                  <div className="dashboard__content-header">
                    <h5>Dashboard</h5>
                  </div>

                  <div className="dashboard__content-info">
                    <div className="dashboard__content-welcome">
                      <div className="dashboard__content-greeting">
                        <h4>
                          Hello, <br /> {user?.data?.name || ''}
                        </h4>
                      </div>
                    </div>

                    <div className="dashboard__content-details">
                      <div className="dashboard__cards mt-4">
                        <div className="row g-4">
                          <div className="col-6 col-md-3">
                            <DashboardCard
                              icon={
                                <HugeiconsIcon
                                  icon={ProductLoadingIcon}
                                  size={32}
                                />
                              }
                              label="My Orders"
                              active={activeCard === 'My Orders'}
                              onClick={() => handleNavigation('my-orders')}
                            />
                          </div>
                          <div className="col-6 col-md-3">
                            <DashboardCard
                              icon={
                                <HugeiconsIcon
                                  icon={UserAccountIcon}
                                  size={32}
                                />
                              }
                              label="Account Details"
                              active={activeCard === 'Account Details'}
                              onClick={() =>
                                handleNavigation('account-details')
                              }
                            />
                          </div>
                          <div className="col-6 col-md-3">
                            <DashboardCard
                              icon={
                                <HugeiconsIcon icon={Shield01Icon} size={32} />
                              }
                              label="Verify Product"
                              active={activeCard === 'Verify Product'}
                              onClick={() => handleNavigation('verify-product')}
                            />
                          </div>
                          <div className="col-6 col-md-3">
                            <DashboardCard
                              icon={
                                <HugeiconsIcon icon={Coupon02Icon} size={32} />
                              }
                              label="My Coupons"
                              active={activeCard === 'My Coupons'}
                              onClick={() => handleNavigation('my-coupons')}
                            />
                          </div>
                          <div className="col-6 col-md-3">
                            <DashboardCard
                              icon={
                                <HugeiconsIcon icon={FavouriteIcon} size={32} />
                              }
                              label="My WishList"
                              active={activeCard === 'My WishList'}
                              onClick={() => handleNavigation('my-wishlist')}
                            />
                          </div>

                          <div className="col-6 col-md-3">
                            <DashboardCard
                              icon={<HugeiconsIcon icon={GiftIcon} size={32} />}
                              label="My Rewards"
                              active={activeCard === 'My Rewards'}
                              onClick={() => handleNavigation('my-rewards')}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="dashboard__logout mt-4 d-lg-none">
                      <ul className="dashboard__menu nav ">
                        <li onClick={() => logout()} className="nav-item">
                          <p className="nav-link dashboard__menu-item">
                            <HugeiconsIcon icon={Logout01Icon} size={20} />{' '}
                            Logout
                          </p>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default UserDashboard;
